import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Instagram, ArrowUpRight } from "lucide-react";

export const INSTAGRAM_URL = "https://www.instagram.com/ultimatefootballraffles/";
const HANDLE = "@ultimatefootballraffles";

export default function InstagramSection() {
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    api.get("/instagram").then((r) => setPhotos(r.data || [])).catch(() => {});
  }, []);

  // Show up to 6 tiles; fill remaining with branded placeholders so the grid always looks full
  const tiles = [...photos].slice(0, 6);
  const placeholders = Math.max(0, 6 - tiles.length);

  return (
    <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24" data-testid="instagram-section">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--ufr-green)] font-bold flex items-center gap-2">
            <Instagram size={14} /> Follow the journey
          </div>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-2">On the gram</h2>
          <p className="text-white/60 text-sm mt-2">Live pulls, big wins and behind-the-scenes — straight from {HANDLE}.</p>
        </div>
        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-ufr-primary px-6 py-3 text-sm inline-flex items-center gap-2 self-start sm:self-auto"
          data-testid="instagram-follow-btn"
        >
          <Instagram size={16} /> Follow {HANDLE.replace("@", "")}
        </a>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="instagram-grid">
        {tiles.map((p) => (
          <a
            key={p.id}
            href={p.link || INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative aspect-square overflow-hidden rounded-md border border-white/10 ufr-ig-tile bg-black"
            data-testid={`instagram-tile-${p.id}`}
          >
            {p.shortcode ? (
              <iframe
                src={`https://www.instagram.com/p/${p.shortcode}/embed/`}
                title="Instagram post"
                className="absolute w-full border-0 pointer-events-none"
                style={{ height: "calc(100% + 110px)", top: -55 }}
                scrolling="no"
                loading="lazy"
                tabIndex={-1}
              />
            ) : (
              <img src={p.image_url} alt={p.caption || "Instagram post"} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
            )}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center">
              <Instagram size={22} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </a>
        ))}
        {Array.from({ length: placeholders }).map((_, i) => (
          <a
            key={`ph-${i}`}
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative aspect-square overflow-hidden rounded-md border border-white/10 flex items-center justify-center"
            style={{ background: "linear-gradient(135deg, rgba(34,255,102,0.08), rgba(0,0,0,0.6))" }}
            data-testid={`instagram-placeholder-${i}`}
          >
            <Instagram size={22} className="text-white/30 group-hover:text-[color:var(--ufr-green)] transition-colors" />
            <ArrowUpRight size={14} className="absolute top-2 right-2 text-white/20 group-hover:text-white/60 transition-colors" />
          </a>
        ))}
      </div>
    </section>
  );
}
